import { useCallback } from "react"
import { useClient } from "urql"

import type {
  GetPriceListDownloadUrlFailure,
  GetPriceListDownloadUrlInput,
  GetPriceListDownloadUrlQuery,
  GetPriceListDownloadUrlQueryVariables,
} from "@/graphql/codegen/graphql"
import { GetPriceListDownloadUrlDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseDownloadPriceListReturn {
  getDownloadUrl: (input: GetPriceListDownloadUrlInput) => Promise<string | undefined>
}

export const useGetDownloadPriceList = (): UseDownloadPriceListReturn => {
  const client = useClient()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const getDownloadUrl = useCallback(
    async (input: GetPriceListDownloadUrlInput): Promise<string | undefined> => {
      const { data, error } = await client
        .query<
          GetPriceListDownloadUrlQuery,
          GetPriceListDownloadUrlQueryVariables
        >(GetPriceListDownloadUrlDocument, { input }, { requestPolicy: "network-only" })
        .toPromise()

      const output = data?.priceList?.getPriceListDownloadUrl

      const hasError = error || output?.__typename === "GetPriceListDownloadUrlFailure"

      if (hasError) {
        const message =
          error?.message ??
          (output as GetPriceListDownloadUrlFailure | undefined)?.error.message ??
          "Failed to generate the price list"

        notify({
          type: NotificationType.Error,
          message,
        })

        return undefined
      }

      return output?.downloadUrl ?? undefined
    },
    [client, notify]
  )

  return { getDownloadUrl }
}
